'use client';
import React, { useState, useEffect, useCallback, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import Badge from '@/components/ui/Badge';
import {
  Search, X, Loader2, BookOpen, Mic, FileText, Layers,
  ScrollText, ChevronLeft, ChevronRight, SlidersHorizontal, Filter,
  MessageSquare, HelpCircle, Users, TrendingUp, Star, Award, CheckCircle2
} from 'lucide-react';
import {
  searchContributions, searchForumPosts, searchAMAQuestions,
  searchContributors, searchWallTopics, getErasAndTopics, getTrendingResults,
  ERA_LABELS, CERTAINTY_OPTIONS, SEARCH_TABS,
  type SearchResult, type WallSearchResult, type ForumPostResult,
  type AMAQuestionResult, type ContributorResult, type SearchTab,
} from '@/lib/db/search';
import Icon from '@/components/ui/AppIcon';


// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 12;

const CERTAINTY_META: Record<string, { label: string; icon: React.ElementType; badge: 'oral' | 'sourced' | 'firsthand' }> = {
  oral_tradition:    { label: 'Oral Tradition',    icon: Mic,      badge: 'oral' },
  sourced_claim:     { label: 'Sourced Claim',     icon: BookOpen, badge: 'sourced' },
  firsthand_account: { label: 'Firsthand Account', icon: FileText, badge: 'firsthand' },
};

const TIER_COLORS: Record<string, string> = {
  elder: '#F39C12',
  historian: '#E05A47',
  trusted: '#008080',
  new: '#5A5A5A',
};

// ─── Highlight ────────────────────────────────────────────────────────────────

function HighlightText({ text, query }: { text: string; query: string }) {
  if (!query.trim() || !text) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} style={{ backgroundColor: 'rgba(224,90,71,0.20)', color: 'var(--primary)', borderRadius: '2px' }}>
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

// ─── Result Cards ─────────────────────────────────────────────────────────────

function CardShell({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="group flex flex-col rounded-xl border overflow-hidden transition-all duration-200 hover:-translate-y-0.5"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--primary)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 4px 24px rgba(224,90,71,0.10)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
      }}
    >
      {children}
    </Link>
  );
}

function ContributionResultCard({ item, query }: { item: SearchResult; query: string }) {
  const meta = CERTAINTY_META[item.certainty_label] || CERTAINTY_META.oral_tradition;
  const date = new Date(item.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const snippet = item.excerpt || item.body_snippet || '';

  return (
    <CardShell href={`/contributions/${item.id}`}>
      <div className="h-0.5 w-full" style={{ backgroundColor: 'var(--primary)', opacity: 0.6 }} />
      <div className="flex flex-col flex-1 p-5 gap-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <Badge variant={meta.badge} />
          <div className="flex items-center gap-1.5 flex-wrap">
            {item.era_id && ERA_LABELS[item.era_id] && (
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                {ERA_LABELS[item.era_id]}
              </span>
            )}
            <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: 'rgba(193,122,62,0.1)', color: 'var(--primary)' }}>
              <ScrollText size={10} />
              Contribution
            </span>
          </div>
        </div>
        <h3 className="text-sm font-semibold leading-snug line-clamp-2" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}>
          <HighlightText text={item.title} query={query} />
        </h3>
        {snippet && (
          <p className="text-xs line-clamp-3 flex-1" style={{ color: 'var(--muted-foreground)' }}>
            <HighlightText text={snippet} query={query} />
          </p>
        )}
        <div className="flex items-center justify-between pt-1 border-t text-xs" style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}>
          <span>{item.is_anonymous ? 'Anonymous' : (item.author_name || 'Unknown')}</span>
          <div className="flex items-center gap-2">
            {(item.corroboration_count ?? 0) > 0 && (
              <span className="flex items-center gap-0.5" style={{ color: '#22c55e' }}>
                <CheckCircle2 size={10} />
                {item.corroboration_count}
              </span>
            )}
            <span>{date}</span>
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function ForumPostCard({ item, query }: { item: ForumPostResult; query: string }) {
  const date = new Date(item.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <CardShell href="/community">
      <div className="h-0.5 w-full" style={{ backgroundColor: '#6366f1', opacity: 0.6 }} />
      <div className="flex flex-col flex-1 p-5 gap-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: 'rgba(99,102,241,0.12)', color: '#6366f1' }}>
            <MessageSquare size={10} />
            Forum Post
          </span>
          {item.era_label && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>
              {item.era_label}
            </span>
          )}
        </div>
        <h3 className="text-sm font-semibold leading-snug line-clamp-2" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}>
          <HighlightText text={item.title} query={query} />
        </h3>
        {item.body_snippet && (
          <p className="text-xs line-clamp-3 flex-1" style={{ color: 'var(--muted-foreground)' }}>
            <HighlightText text={item.body_snippet} query={query} />
          </p>
        )}
        <div className="flex items-center justify-between pt-1 border-t text-xs" style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}>
          <span>{item.author_name || 'Anonymous'}</span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-0.5">
              <TrendingUp size={10} />
              {item.upvote_count}
            </span>
            <span className="flex items-center gap-0.5">
              <MessageSquare size={10} />
              {item.reply_count}
            </span>
            <span>{date}</span>
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function AMAQuestionCard({ item, query }: { item: AMAQuestionResult; query: string }) {
  const date = new Date(item.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const isAnswered = !!item.answer;
  return (
    <CardShell href="/community">
      <div className="h-0.5 w-full" style={{ backgroundColor: isAnswered ? '#22c55e' : '#f59e0b', opacity: 0.7 }} />
      <div className="flex flex-col flex-1 p-5 gap-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>
              <HelpCircle size={10} />
              AMA
            </span>
            {item.is_featured && (
              <span className="text-xs px-1.5 py-0.5 rounded-full flex items-center gap-0.5" style={{ backgroundColor: 'rgba(193,122,62,0.15)', color: 'var(--primary)' }}>
                <Star size={9} />
                Featured
              </span>
            )}
          </div>
          {isAnswered ? (
            <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: 'rgba(34,197,94,0.12)', color: '#22c55e' }}>
              <CheckCircle2 size={10} />
              Answered
            </span>
          ) : (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>
              Pending
            </span>
          )}
        </div>
        <p className="text-sm font-medium leading-snug line-clamp-2" style={{ color: 'var(--foreground)' }}>
          <HighlightText text={item.question} query={query} />
        </p>
        {item.answer && (
          <p className="text-xs line-clamp-2 flex-1 pl-2 border-l-2" style={{ color: 'var(--muted-foreground)', borderColor: '#22c55e' }}>
            <HighlightText text={item.answer} query={query} />
          </p>
        )}
        <div className="flex items-center justify-between pt-1 border-t text-xs" style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}>
          <span className="truncate max-w-[120px]" title={item.ama_title}>{item.ama_title}</span>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-0.5">
              <TrendingUp size={10} />
              {item.upvote_count}
            </span>
            <span>{date}</span>
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function ContributorCard({ item, query }: { item: ContributorResult; query: string }) {
  const tierColor = TIER_COLORS[item.tier] || TIER_COLORS.new;
  const initials = item.full_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  return (
    <CardShell href={`/contributor-profile?id=${item.id}`}>
      <div className="h-0.5 w-full" style={{ backgroundColor: tierColor, opacity: 0.7 }} />
      <div className="flex flex-col flex-1 p-5 gap-3">
        <div className="flex items-center gap-3">
          {item.avatar_url ? (
            <img src={item.avatar_url} alt={`${item.full_name} avatar`} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold" style={{ backgroundColor: `${tierColor}22`, color: tierColor }}>
              {initials}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>
              <HighlightText text={item.full_name} query={query} />
            </p>
            {item.username && (
              <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>@{item.username}</p>
            )}
          </div>
          <span className="text-xs px-2 py-0.5 rounded-full capitalize flex-shrink-0" style={{ backgroundColor: `${tierColor}18`, color: tierColor }}>
            {item.tier}
          </span>
        </div>
        {item.bio && (
          <p className="text-xs line-clamp-2" style={{ color: 'var(--muted-foreground)' }}>
            <HighlightText text={item.bio} query={query} />
          </p>
        )}
        {item.specialisations?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.specialisations.slice(0, 3).map((s, i) => (
              <span key={i} className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>{s}</span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between pt-1 border-t text-xs" style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}>
          <span className="flex items-center gap-1">
            <Award size={10} />
            {item.reputation_score} rep
          </span>
          <span className="flex items-center gap-1">
            <ScrollText size={10} />
            {item.published_count} published
          </span>
        </div>
      </div>
    </CardShell>
  );
}

function WallResultCard({ item, query }: { item: WallSearchResult; query: string }) {
  const pct = Math.min(100, Math.round((item.published / Math.max(item.min_coverage, 1)) * 100));
  const gapColor = pct >= 100 ? '#22c55e' : pct >= 50 ? 'var(--primary)' : '#ef4444';

  return (
    <CardShell href="/the-wall">
      <div className="h-0.5 w-full" style={{ backgroundColor: gapColor, opacity: 0.7 }} />
      <div className="flex flex-col flex-1 p-5 gap-3">
        <div className="flex items-start justify-between gap-2">
          <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: 'rgba(193,122,62,0.1)', color: 'var(--primary)' }}>
            <Layers size={10} />
            Wall Topic
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>
            {item.era_label}
          </span>
        </div>
        <h3 className="text-sm font-semibold leading-snug" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}>
          <HighlightText text={item.topic_label} query={query} />
        </h3>
        <div className="flex flex-col gap-1.5">
          <div className="flex justify-between text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <span>{item.published} published</span>
            <span>{pct}% of {item.min_coverage} target</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--muted)' }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: gapColor }} />
          </div>
        </div>
      </div>
    </CardShell>
  );
}

// ─── Trending Panel ───────────────────────────────────────────────────────────

function TrendingPanel({ onSearch }: { onSearch: (q: string) => void }) {
  const [trending, setTrending] = useState<{ contributions: SearchResult[]; forumPosts: ForumPostResult[]; amaQuestions: AMAQuestionResult[] } | null>(null);

  useEffect(() => {
    getTrendingResults().then(setTrending);
  }, []);

  if (!trending) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 size={28} className="animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Trending Contributions */}
      {trending.contributions.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={14} style={{ color: 'var(--primary)' }} />
            <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>Trending Contributions</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {trending.contributions.map(item => (
              <ContributionResultCard key={item.id} item={item} query="" />
            ))}
          </div>
        </section>
      )}

      {/* Trending Forum Posts */}
      {trending.forumPosts.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare size={14} style={{ color: '#6366f1' }} />
            <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>Active Forum Discussions</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {trending.forumPosts.map(item => (
              <ForumPostCard key={item.id} item={item} query="" />
            ))}
          </div>
        </section>
      )}

      {/* Trending AMA Questions */}
      {trending.amaQuestions.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle size={14} style={{ color: '#f59e0b' }} />
            <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>Featured AMA Questions</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {trending.amaQuestions.map(item => (
              <AMAQuestionCard key={item.id} item={item} query="" />
            ))}
          </div>
        </section>
      )}

      {trending.contributions.length === 0 && trending.forumPosts.length === 0 && trending.amaQuestions.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <TrendingUp size={32} style={{ color: 'var(--muted-foreground)' }} />
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No trending content yet. Be the first to contribute!</p>
        </div>
      )}
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function Pagination({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (p: number) => void }) {
  if (totalPages <= 1) return null;
  const pages: (number | '...')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
  }

  return (
    <div className="flex items-center justify-center gap-1 mt-8">
      <button
        onClick={() => onPage(page - 1)}
        disabled={page === 1}
        className="p-2 rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)', backgroundColor: 'var(--card)' }}
      >
        <ChevronLeft size={16} />
      </button>
      {pages.map((p, i) =>
        p === '...' ? (
          <span key={`ellipsis-${i}`} className="px-2 text-sm" style={{ color: 'var(--muted-foreground)' }}>…</span>
        ) : (
          <button
            key={p}
            onClick={() => onPage(p as number)}
            className="w-9 h-9 rounded-lg border text-sm font-medium transition-all"
            style={{
              borderColor: p === page ? 'var(--primary)' : 'var(--border)',
              backgroundColor: p === page ? 'rgba(193,122,62,0.15)' : 'var(--card)',
              color: p === page ? 'var(--primary)' : 'var(--foreground)',
            }}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onPage(page + 1)}
        disabled={page === totalPages}
        className="p-2 rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)', backgroundColor: 'var(--card)' }}
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState({ query }: { query: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'var(--muted)' }}>
        <Search size={28} style={{ color: 'var(--muted-foreground)' }} />
      </div>
      <div className="text-center">
        <p className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>
          {query ? `No results for "${query}"` : 'No results found'}
        </p>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Try adjusting your search terms or clearing filters.
        </p>
      </div>
    </div>
  );
}

// ─── Tab Icon Map ─────────────────────────────────────────────────────────────

const TAB_ICONS: Record<SearchTab, React.ElementType> = {
  contributions: ScrollText,
  forum: MessageSquare,
  ama: HelpCircle,
  contributors: Users,
  wall: Layers,
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SearchPage() {
  return (
    <Suspense fallback={
      <AppLayout>
        <div className="min-h-screen pt-24 flex items-center justify-center">
          <Loader2 size={32} className="animate-spin" style={{ color: 'var(--primary)' }} />
        </div>
      </AppLayout>
    }>
      <SearchPageInner />
    </Suspense>
  );
}

function SearchPageInner() {
  const searchParams = useSearchParams();

  const [query, setQuery] = useState(searchParams?.get('q') || '');
  const [inputValue, setInputValue] = useState(searchParams?.get('q') || '');
  const [eraFilter, setEraFilter] = useState(searchParams?.get('era') || '');
  const [topicFilter, setTopicFilter] = useState(searchParams?.get('topic') || '');
  const [certaintyFilter, setCertaintyFilter] = useState(searchParams?.get('certainty') || '');
  const [page, setPage] = useState(Number(searchParams?.get('page') || 1));
  const [tab, setTab] = useState<SearchTab>(
    (searchParams?.get('tab') as SearchTab) || 'contributions'
  );

  const [contributions, setContributions] = useState<SearchResult[]>([]);
  const [forumPosts, setForumPosts] = useState<ForumPostResult[]>([]);
  const [amaQuestions, setAMAQuestions] = useState<AMAQuestionResult[]>([]);
  const [contributors, setContributors] = useState<ContributorResult[]>([]);
  const [wallItems, setWallItems] = useState<WallSearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [eras, setEras] = useState<{ id: string; label: string }[]>([]);
  const [topics, setTopics] = useState<{ id: string; era_id: string; label: string }[]>([]);

  const filteredTopics = eraFilter ? topics.filter((t) => t.era_id === eraFilter) : topics;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const activeFilters = [eraFilter, topicFilter, certaintyFilter].filter(Boolean).length;
  const showFilters = tab !== 'contributors';

  useEffect(() => {
    getErasAndTopics().then(({ eras, topics }) => {
      setEras(eras);
      setTopics(topics);
    });
  }, []);

  const runSearch = useCallback(async (
    q: string, era: string, topic: string, certainty: string, pg: number, t: SearchTab
  ) => {
    setLoading(true);
    try {
      if (t === 'contributions') {
        const { data, total } = await searchContributions({ query: q, era: era || undefined, topic: topic || undefined, certainty: certainty || undefined, page: pg, pageSize: PAGE_SIZE });
        setContributions(data);
        setTotal(total);
      } else if (t === 'forum') {
        const { data, total } = await searchForumPosts({ query: q, era: era || undefined, page: pg, pageSize: PAGE_SIZE });
        setForumPosts(data);
        setTotal(total);
      } else if (t === 'ama') {
        const { data, total } = await searchAMAQuestions({ query: q, era: era || undefined, page: pg, pageSize: PAGE_SIZE });
        setAMAQuestions(data);
        setTotal(total);
      } else if (t === 'contributors') {
        const { data, total } = await searchContributors({ query: q, page: pg, pageSize: PAGE_SIZE });
        setContributors(data);
        setTotal(total);
      } else {
        const data = await searchWallTopics(q, era || undefined);
        setWallItems(data);
        setTotal(data.length);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (query.trim() || activeFilters > 0) {
      runSearch(query, eraFilter, topicFilter, certaintyFilter, page, tab);
    }
  }, [query, eraFilter, topicFilter, certaintyFilter, page, tab, runSearch, activeFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setQuery(inputValue);
  };

  const clearFilters = () => {
    setEraFilter('');
    setTopicFilter('');
    setCertaintyFilter('');
    setPage(1);
  };

  const handleTabChange = (t: SearchTab) => {
    setTab(t);
    setPage(1);
  };

  const handlePage = (p: number) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasQuery = query.trim().length > 0 || activeFilters > 0;

  return (
    <AppLayout>
      <div className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <Search size={18} style={{ color: 'var(--primary)' }} />
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>
              Archive Search
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}>
            Search the Archive
          </h1>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            Full-text search across contributions, forum posts, AMA questions, contributors, and Wall topics.
          </p>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Search titles, questions, contributors, topics…"
                className="w-full pl-10 pr-10 py-3 rounded-xl border text-sm outline-none transition-colors"
                style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
              />
              {inputValue && (
                <button
                  type="button"
                  onClick={() => { setInputValue(''); setQuery(''); setPage(1); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  <X size={15} />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="px-5 py-3 rounded-xl text-sm font-semibold transition-all active:scale-95"
              style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
            >
              Search
            </button>
            {showFilters && (
              <button
                type="button"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className="relative px-4 py-3 rounded-xl border text-sm font-medium transition-colors flex items-center gap-1.5"
                style={{
                  borderColor: activeFilters > 0 ? 'var(--primary)' : 'var(--border)',
                  backgroundColor: activeFilters > 0 ? 'rgba(193,122,62,0.1)' : 'var(--card)',
                  color: activeFilters > 0 ? 'var(--primary)' : 'var(--muted-foreground)',
                }}
              >
                <SlidersHorizontal size={15} />
                <span className="hidden sm:inline">Filters</span>
                {activeFilters > 0 && (
                  <span className="w-4 h-4 rounded-full text-xs flex items-center justify-center font-bold" style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                    {activeFilters}
                  </span>
                )}
              </button>
            )}
          </div>
        </form>

        {/* Filters panel */}
        {filtersOpen && showFilters && (
          <div className="mb-6 p-4 rounded-xl border" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: 'var(--muted-foreground)' }}>
                <Filter size={12} /> Refine Results
              </span>
              {activeFilters > 0 && (
                <button onClick={clearFilters} className="text-xs flex items-center gap-1" style={{ color: 'var(--primary)' }}>
                  <X size={11} /> Clear all
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Era filter */}
              <div>
                <label className="block text-xs mb-1.5 font-medium" style={{ color: 'var(--muted-foreground)' }}>Era</label>
                <select
                  value={eraFilter}
                  onChange={(e) => { setEraFilter(e.target.value); setTopicFilter(''); setPage(1); }}
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                  style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                >
                  <option value="">All Eras</option>
                  {eras.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
                </select>
              </div>
              {/* Topic filter — only for contributions + wall */}
              {(tab === 'contributions' || tab === 'wall') && (
                <div>
                  <label className="block text-xs mb-1.5 font-medium" style={{ color: 'var(--muted-foreground)' }}>Topic</label>
                  <select
                    value={topicFilter}
                    onChange={(e) => { setTopicFilter(e.target.value); setPage(1); }}
                    className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                    style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                  >
                    <option value="">All Topics</option>
                    {filteredTopics.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
              )}
              {/* Certainty filter — only for contributions */}
              {tab === 'contributions' && (
                <div>
                  <label className="block text-xs mb-1.5 font-medium" style={{ color: 'var(--muted-foreground)' }}>Certainty Label</label>
                  <select
                    value={certaintyFilter}
                    onChange={(e) => { setCertaintyFilter(e.target.value); setPage(1); }}
                    className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                    style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                  >
                    <option value="">All Labels</option>
                    {CERTAINTY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-0.5 mb-6 border-b overflow-x-auto" style={{ borderColor: 'var(--border)' }}>
          {SEARCH_TABS.map((t) => {
            const Icon = TAB_ICONS[t.key];
            return (
              <button
                key={t.key}
                onClick={() => handleTabChange(t.key)}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap"
                style={{
                  borderColor: tab === t.key ? 'var(--primary)' : 'transparent',
                  color: tab === t.key ? 'var(--primary)' : 'var(--muted-foreground)',
                }}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Results meta */}
        {hasQuery && (
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              {loading ? (
                <span className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Searching…</span>
              ) : (
                <>
                  <span className="font-semibold" style={{ color: 'var(--foreground)' }}>{total.toLocaleString()}</span>
                  {' '}result{total !== 1 ? 's' : ''} found
                  {query && <> for <span className="font-medium" style={{ color: 'var(--primary)' }}>"{query}"</span></>}
                </>
              )}
            </p>
            {total > 0 && totalPages > 1 && (
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                Page {page} of {totalPages}
              </p>
            )}
          </div>
        )}

        {/* Content */}
        {!hasQuery ? (
          /* Trending panel when no query */
          <TrendingPanel onSearch={(q) => { setInputValue(q); setQuery(q); }} />
        ) : loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--primary)' }} />
          </div>
        ) : (
          <>
            {tab === 'contributions' && (
              <>
                {contributions.length === 0 ? <EmptyState query={query} /> : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {contributions.map((item) => <ContributionResultCard key={item.id} item={item} query={query} />)}
                  </div>
                )}
                <Pagination page={page} totalPages={totalPages} onPage={handlePage} />
              </>
            )}

            {tab === 'forum' && (
              <>
                {forumPosts.length === 0 ? <EmptyState query={query} /> : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {forumPosts.map((item) => <ForumPostCard key={item.id} item={item} query={query} />)}
                  </div>
                )}
                <Pagination page={page} totalPages={totalPages} onPage={handlePage} />
              </>
            )}

            {tab === 'ama' && (
              <>
                {amaQuestions.length === 0 ? <EmptyState query={query} /> : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {amaQuestions.map((item) => <AMAQuestionCard key={item.id} item={item} query={query} />)}
                  </div>
                )}
                <Pagination page={page} totalPages={totalPages} onPage={handlePage} />
              </>
            )}

            {tab === 'contributors' && (
              <>
                {contributors.length === 0 ? <EmptyState query={query} /> : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {contributors.map((item) => <ContributorCard key={item.id} item={item} query={query} />)}
                  </div>
                )}
                <Pagination page={page} totalPages={totalPages} onPage={handlePage} />
              </>
            )}

            {tab === 'wall' && (
              <>
                {wallItems.length === 0 ? <EmptyState query={query} /> : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {wallItems.map((item) => <WallResultCard key={item.id} item={item} query={query} />)}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
